/**
 * Server-Sent Events (SSE) Stream Parsing Utilities
 * Adapted from Ocular's NDJSON parser for SSE format
 * Based on: https://github.com/OcularEngineering/ocular/blob/main/packages/ocular-ui/lib/stream.ts
 *
 * SSE Format:
 * data: content here\n\n
 * data: more content\n\n
 * data: [DONE]\n\n
 */

/**
 * TransformStream that parses Server-Sent Events (SSE) format
 * Handles incomplete chunks and extracts content from "data: " prefix
 */
class SseParserStream extends TransformStream<string, string> {
  private buffer: string = ''

  constructor() {
    let controller: TransformStreamDefaultController<string>
    super({
      start: (_controller) => {
        controller = _controller
      },
      transform: (chunk) => {
        try {
          // Add chunk to buffer
          this.buffer += chunk

          // Split by SSE message delimiter (double newline)
          const messages = this.buffer.split('\n\n')

          // Keep last incomplete message in buffer
          this.buffer = messages.pop() || ''

          // Process complete messages
          for (const message of messages) {
            if (!message.trim()) continue

            // Parse SSE message lines
            const lines = message.split('\n')
            let data = ''

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                data += line.slice(6) // Remove "data: " prefix
              }
            }

            // Skip empty data, [DONE] marker, and usage events (billing info)
            if (data && data !== '[DONE]' && !data.includes('"type":"usage"') && !data.includes('"type": "usage"')) {
              // Regular content - unescape newlines that were escaped by backend for SSE transport
              const unescaped = data.replace(/\\n/g, '\n').replace(/\\r/g, '\r')
              controller.enqueue(unescaped)
            }
          }
        } catch (error) {
          console.error('SSE Parser transform error:', error)
        }
      },
      flush: () => {
        // Flush is called when stream ends - any remaining buffer content is discarded
        // as it's typically incomplete SSE messages or usage events we don't need
      }
    })
  }
}

/**
 * Creates a reader from a Response body that parses SSE format
 * Pipes through TextDecoderStream and SseParserStream
 *
 * @param responseBody - ReadableStream from fetch Response.body
 * @returns ReadableStreamDefaultReader that yields parsed SSE content
 */
export function createReader(responseBody: ReadableStream<Uint8Array> | null) {
  return responseBody
    ?.pipeThrough(new TextDecoderStream() as unknown as TransformStream<Uint8Array, string>)
    .pipeThrough(new SseParserStream())
    .getReader()
}

/**
 * Async generator that reads from a stream reader
 * Yields each chunk directly without delay
 *
 * @param reader - ReadableStreamDefaultReader from createReader()
 * @yields Parsed content chunks
 */
export async function* readStream<T>(reader: any): AsyncGenerator<T, void> {
  if (!reader) {
    throw new Error('No response body or body is not readable')
  }

  while (true) {
    const { value, done } = await reader.read()

    if (done) {
      break
    }

    if (value) {
      yield value as T
    }
  }
}
